import { useId, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { FormField } from '@noor/ui';
import { cn } from '@noor/lib/cn';
import type { BranchFormValues } from '../model/schema';
import { CoordinatesRow } from './coordinates-row';
import { AddressSearchInput, type PickedAddress } from './address-search-input';
import { LocationPickerDialog } from './location-picker-dialog';

interface AddressFieldMobileProps {
  label: string;
  placeholder: string;
  /** Accessible name for the pin toggle — the design shows no text beside it. */
  pickLabel: string;
  /** The sheet's shared filled-pill treatment. */
  fieldClassName: string;
}

/**
 * The «Адрес» row on mobile (Figma node 26614:20738): a filled grey pill whose
 * trailing edge carries an ORANGE OUTLINE PIN, and nothing else.
 *
 * That pin is the whole difference from the desktop `AddressField`, which puts
 * a text link («Указать на карте») inside the pill instead. On a phone the
 * link ate roughly half the field's width, leaving the address itself a few
 * visible characters — so the design replaces it with a glyph. Tapping opens
 * the same `LocationPickerDialog` the desktop field uses, full-width on a
 * small viewport via the dialog's own responsive max-width.
 *
 * The pin is drawn here rather than pulled from `@noor/ui`: the exported
 * `MenuLocationIcon` is a FILLED marker (the branch selector's leading glyph),
 * where this design's mark is a 1.5px outline pin with a hollow centre.
 *
 * The input is READ-ONLY and the picked point renders beneath it, for the same
 * reasons as the desktop `AddressField` — see its doc comment. The coordinates
 * keep their copy glyph permanently visible here, since touch has no hover.
 */
export function AddressFieldMobile({
  label,
  placeholder,
  pickLabel,
  fieldClassName,
}: AddressFieldMobileProps) {
  const { control, setValue } = useFormContext<BranchFormValues>();
  const address = useWatch({ control, name: 'address' });
  const latitude = useWatch({ control, name: 'latitude' });
  const longitude = useWatch({ control, name: 'longitude' });
  const [isPicking, setIsPicking] = useState(false);
  const inputId = useId();
  // The pill's border lives on the WRAPPER, so the invalid state is read here
  // — the input's own border is stripped away.
  const { errors } = useFormState({ control, name: 'address' });
  const isInvalid = errors.address !== undefined;

  // Coordinates first: `address`'s cross-field validation (the "place the
  // branch" check) reads latitude/longitude, so writing it last is what lets
  // that check see the NEW point rather than the stale one still in form state.
  const commit = (result: PickedAddress) => {
    setValue('latitude', result.latitude, { shouldValidate: true });
    setValue('longitude', result.longitude, { shouldValidate: true });
    setValue('address', result.address, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <FormField name="address" className="gap-2">
      <FormField.Label
        htmlFor={inputId}
        className="flex items-center gap-px px-1 text-sm font-medium tracking-tight text-secondary-foreground"
      >
        {label}
      </FormField.Label>

      {/* `cn` (not string concatenation): the invalid border has to BEAT the
          borderless base in `fieldClassName`, and tailwind-merge is what drops
          the losing class rather than leaving both for stylesheet order to
          settle. */}
      <div
        className={cn(
          fieldClassName,
          'relative flex items-center gap-2 px-4',
          isInvalid && 'border border-destructive',
        )}
      >
        {/* `text-ellipsis` for the same reason as the desktop field: an
            `<input>` clips its overflow with no marker, so a long address
            stopped mid-word against the pin instead of reading as truncated.
            More pressing here — a phone column is narrower than the modal's. */}
        <AddressSearchInput
          id={inputId}
          value={address}
          invalid={isInvalid}
          className="h-9 min-w-0 flex-1 border-none bg-transparent p-0 text-sm leading-9 text-ellipsis shadow-none outline-none focus-visible:border-none focus-visible:ring-0"
          placeholder={placeholder}
          onSelect={commit}
        />
        <button
          type="button"
          onClick={() => setIsPicking(true)}
          aria-label={pickLabel}
          className="shrink-0 rounded-full text-primary transition-transform outline-none hover:cursor-pointer focus-visible:ring-[3px] focus-visible:ring-ring/40 active:scale-95"
        >
          <PinIcon className="size-6" />
        </button>
      </div>

      <CoordinatesRow latitude={latitude} longitude={longitude} alwaysShowIcon />

      <FormField.Message className="min-h-0 px-1 empty:hidden" />

      <LocationPickerDialog
        open={isPicking}
        onOpenChange={setIsPicking}
        address={address}
        latitude={latitude}
        longitude={longitude}
        onConfirm={commit}
      />
    </FormField>
  );
}

/** Outline map pin, 1.5px stroke (Figma «Navigation, Maps/Pin, Location»). */
function PinIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M12 13c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3Z" />
      <path d="M12 21s-7-5.75-7-11a7 7 0 1 1 14 0c0 5.25-7 11-7 11Z" />
    </svg>
  );
}
